import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { isEqual } from 'lodash';
import {
  CodeEditorActivity,
  CodeEditorTestCase,
  LanguageCodeEditorEnum,
} from '@/models/CodeEditorActivity';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import EnumInput from '@/components/ui/custom/dataInput/EnumInput';
import MyCkeditor from '@/components/editor/MyCkeditor';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ImageFolderEnum } from '@/models/CkEditor';
import TriggerSizeCodeEditor from '@/components/codeEditor/TriggerSizeCodeEditor';
import Text from '@/components/ui/custom/dataDisplay/Text';

import { CodeValidationResult as ValidationResult } from '@/models/CodeValidationResult';
import { CodeValidationResult } from '../../preview/codeEditor/validation/CodeValidationResult';
import { LoadingIcon } from '@/components/icons';
import { ActivityValidationResult } from '@/data/services/activityElement/codeEditorActivityServices';

import TestCasesForm from './TestCaseForm';

export type CodeEditorActivityFields = Pick<
  CodeEditorActivity,
  'title' | 'content' | 'initialCode' | 'solutionCode' | 'language'
>;

export type TestCaseFields = Omit<
  CodeEditorTestCase,
  'id' | 'codeEditorActivityId'
> & {
  id?: number;
};

export type CodeEditorFormFields = CodeEditorActivityFields & {
  testCases?: TestCaseFields[];
};

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export type PartialCodeEditorFormFields =
  RecursivePartial<CodeEditorFormFields>;

export type FormValidationResult = {
  isValidating?: boolean;
  results?: ValidationResult[];
};

type CodeEditorFormProps = {
  defaultValues?: CodeEditorFormFields;
  onSubmit: (data: CodeEditorFormFields) => Promise<void>;
  onChange: (data: PartialCodeEditorFormFields) => void;
  onValidate?: (
    data: CodeEditorFormFields,
  ) => Promise<ActivityValidationResult>;
  isSubmitting?: boolean;
  validation?: FormValidationResult;
  startsEnabled?: boolean;
};

export default function CodeEditorForm({
  defaultValues,
  onSubmit,
  onChange,
  isSubmitting,
  onValidate,
  validation,
  startsEnabled = false,
}: CodeEditorFormProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [tCodeEditorLanguage] = useTranslation('translation', {
    keyPrefix: 'codeEditorLanguage',
  });

  const [tCodeEditorForm] = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });

  const [hasChanges, setHasChanges] = useState(startsEnabled);

  async function submitData(data: CodeEditorFormFields) {
    await onSubmit(data);
    setHasChanges(false);
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    trigger,
  } = useForm<CodeEditorFormFields>({
    defaultValues,
  });

  async function handleValidation() {
    const formValidations = await Promise.all([
      trigger('testCases'),
      trigger('solutionCode'),
    ]);
    const hasInvalidFields = formValidations.some(isValid => !isValid);
    if (hasInvalidFields) return;

    await onValidate?.(watch());
  }

  const language = watch('language') ?? LanguageCodeEditorEnum.JAVASCRIPT;

  const hasTestCases =
    watch('testCases')?.some(({ expectedOutput }) => !!expectedOutput) || false;

  useEffect(() => {
    if (startsEnabled) return;

    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch, startsEnabled]);

  useEffect(() => {
    const w = watch(values => {
      if (isEqual(values, defaultValues)) return;
      onChange(values);
    });
    return () => w.unsubscribe();
  }, [onChange, watch, defaultValues]);

  const blockSave = Object.values(errors).length > 0;

  return (
    <form onSubmit={handleSubmit(submitData)} className="flex flex-col gap-6">
      <TextInput
        testId="titleInput"
        register={register('title', {
          required: t('form.required', {
            field: t('form.title.title').toLowerCase(),
          }),
        })}
        label={t('form.title.title')}
        errorLabelText={errors.title?.message}
      />

      <Controller
        rules={{
          required: t('form.required', {
            field: t('form.content.title').toLowerCase(),
          }),
        }}
        name="content"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <div className="flex flex-col">
            <label
              htmlFor="content"
              className="label leading-none p-0 w-full text-[0.875rem]"
            >
              {t('form.content.title')}
            </label>
            <ConditionalRenderer condition={error?.message}>
              <label className="font-rubik text-error error-label text-[0.875rem]">
                {error?.message}
              </label>
            </ConditionalRenderer>
            <MyCkeditor
              testId="contentEditor"
              content={value || ''}
              folder={ImageFolderEnum.ACTIVITY}
              handleContentChange={onChange}
            />
          </div>
        )}
      />

      <Controller
        rules={{
          required: t('form.required', {
            field: t('form.language.title').toLowerCase(),
          }),
        }}
        name="language"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <EnumInput
            testId="languageInput"
            label={t('form.language.title')}
            selected={value}
            onSelect={onChange}
            enumModel={LanguageCodeEditorEnum}
            displayName={option => tCodeEditorLanguage(option)}
            errorLabelText={error?.message}
            type="string"
          />
        )}
      />

      <Controller
        name="initialCode"
        control={control}
        render={({ field: { onChange, value } }) => (
          <div className="flex flex-col">
            <label
              htmlFor="initialCode"
              className="label leading-none p-0 w-full text-[0.875rem]"
            >
              {tCodeEditorForm('initialCode')}
            </label>
            <TriggerSizeCodeEditor
              testId="initialCodeEditor"
              value={value ?? ''}
              onChangeCode={onChange}
              autocomplete
              language={language}
            />
          </div>
        )}
      />

      <Controller
        name="solutionCode"
        control={control}
        rules={{
          required:
            hasTestCases &&
            t('form.required', {
              field: tCodeEditorForm('solutionCode').toLowerCase(),
            }),
        }}
        render={({ field: { onChange, value } }) => (
          <div className="flex flex-col">
            <label
              htmlFor="initialCode"
              className="label leading-none p-0 w-full text-[0.875rem]"
            >
              {tCodeEditorForm('solutionCode')}
            </label>
            <ConditionalRenderer condition={errors.solutionCode?.message}>
              <Text
                text={errors.solutionCode?.message}
                format="rubik-400"
                className="text-error"
              />
            </ConditionalRenderer>
            <TriggerSizeCodeEditor
              testId="solutionCodeInput"
              value={value ?? ''}
              onChangeCode={onChange}
              autocomplete
              language={language}
            />
          </div>
        )}
      />

      <TestCasesForm
        control={control}
        onValidate={handleValidation}
        validation={validation}
      />

      <ConditionalRenderer
        condition={!validation?.isValidating}
        fallback={<LoadingIcon className="w-24 mx-auto text-primary" />}
      >
        <CodeValidationResult
          language={language}
          results={validation?.results ?? []}
          sourceCode={watch('solutionCode') || ''}
        />
      </ConditionalRenderer>

      <MainButton
        dataTestId="saveButton"
        type="submit"
        icon={<ArrowDownOnSquareIcon />}
        text={t('save')}
        disabled={!hasChanges || blockSave}
        loading={isSubmitting}
      />
    </form>
  );
}
